import { section, cart } from "@/glow";
import { remove } from "@fluorescent/dom";
import { emit } from "@/glow/events";
import playButtonBlock from "@/lib/play-button-block";
import { animateShoppableHero, shouldAnimate } from "@/lib/animation";
import { userIsTabbing } from "@/lib/a11y";

const selectors = {
  productsSlider: "products-slider",
  playButtonVideo: "[data-play-button-block-video]",
  playButtonBlock: ".play-button-block",
  quickAddButton: '[data-quick-shop-trigger="quick-add"]',
  quickViewButton: '[data-quick-shop-trigger="quick-view"]',
  quickCart: ".quick-cart",
  purchaseConfirmation: ".purchase-confirmation-popup",
};

const classes = {
  loading: "loading",
};

const attrs = {
  activeBlockIndex: "data-active-block-index",
  swiperSlideIndex: "data-swiper-slide-index",
};

section("shoppable-hero", {
  onLoad() {
    this.productsSlider = this.container.querySelector(
      selectors.productsSlider
    );
    this.quickAddButtons = Array.from(
      this.container.querySelectorAll(selectors.quickAddButton)
    );
    this.quickViewButtons = Array.from(
      this.container.querySelectorAll(selectors.quickViewButton)
    );
    this.purchaseConfirmation = document.querySelector(
      selectors.purchaseConfirmation
    );
    this.quickCart = document.querySelector(selectors.quickCart);

    const playButtonVideos = Array.from(
      this.container.querySelectorAll(selectors.playButtonVideo)
    );

    if (playButtonVideos.length) {
      this.playButtons = playButtonVideos.map(block =>
        playButtonBlock(block.closest(selectors.playButtonBlock))
      );
    }

    this.eventController = new AbortController();
    this.eventOptions = { signal: this.eventController.signal };

    this.quickAddButtons.forEach(button => {
      button.addEventListener(
        "click",
        e => this._handleQuickAddButton(e),
        this.eventOptions
      );
    });

    this.quickViewButtons.forEach(button => {
      button.addEventListener(
        "click",
        e => this._handleQuickViewButton(e),
        this.eventOptions
      );
    });

    if (shouldAnimate(this.container)) {
      this.animateShoppableHero = animateShoppableHero(this.container);
    }
  },

  _handleQuickAddButton(e) {
    const buttonEl = e.currentTarget;
    buttonEl.classList.add(classes.loading);

    // if quick cart and confirmation popup are disabled, use standard form submit
    if (!this.purchaseConfirmation && !this.quickCart) return;

    e.preventDefault();
    e.stopPropagation();
    const { productId } = buttonEl.dataset;

    if (!productId) return;

    const minQuantity = buttonEl.getAttribute("data-quantity-minimum");

    cart.addItemById(productId, minQuantity).then(
      ({ res }) => {
        buttonEl.classList.remove(classes.loading);

        if (this.purchaseConfirmation && !userIsTabbing()) {
          emit("confirmation-popup:open", null, { product: res.items[0] });
        } else {
          emit("quick-cart:updated");
          // Need a delay to allow quick-cart to refresh
          setTimeout(() => {
            emit("quick-cart:open");
          }, 300);
        }
      },
      error => {
        console.error(error);
        remove(buttonEl, "loading");
        alert(error.message);
        emit("quick-cart:open");
      }
    );
  },

  _handleQuickViewButton(e) {
    e.preventDefault();
    e.stopPropagation();
    const buttonEl = e.currentTarget;
    const { productUrl } = buttonEl.dataset;

    if (!productUrl) return;

    emit("quick-view:open", null, {
      productUrl: productUrl,
    });
  },

  /**
   * Passes the index to the products-slider custom element.
   * @param {Number} index - The block index corresponding with the slide index.
   * @returns {void}
   */
  _setSliderActiveBlockIndex(index) {
    if (this.productsSlider) {
      this.productsSlider.setAttribute(attrs.activeBlockIndex, index);
    }
  },

  onBlockSelect({ target: el }) {
    setTimeout(() => {
      const index = parseInt(el.getAttribute(attrs.swiperSlideIndex), 10);
      this._setSliderActiveBlockIndex(index);
    }, 50);
  },

  onUnload() {
    this.playButtons && this.playButtons.forEach(button => button.unload());
    this.animateShoppableHero?.destroy();
    this.eventController.abort();
  },
});
